import CreativeLandingShell from "@/components/landingV2/CreativeLandingShell";
import { creativeToolRootPaths } from "@shared/creativeTools";
import React from "react";
import {
  Music,
  Mic2,
  MicVocal,
  Captions,
  ListMusic,
  Bell,
  FolderOpen,
  Settings,
  LogOut,
  Menu,
  ChevronDown,
  Radio,
  Loader2,
  Sparkles,
  PlaySquare,
  Star,
  Video,
  Crown,
  Layers,
  Activity,
  Waves,
  Music2,
  Piano,
  Search,
  Link2,
  Scissors,
  Undo2,
  Repeat,
  Volume2,
  Gamepad2,
  SlidersHorizontal,
  Youtube,
  ReceiptText,
  ShoppingBag,
  Gift,
  User as UserIcon,
  PersonStanding,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Link } from "wouter";
import { useAuth } from "@/_core/hooks/useAuth";
import AuthModal from "@/components/AuthModal";
import LanguageSelector from "@/components/LanguageSelector";
import FeedbackDialog from "@/components/FeedbackDialog";
import { SilentErrorBoundary } from "@/components/SilentErrorBoundary";
import CreditHistoryModal from "@/components/CreditHistoryModal";
import InviteFriendsDialog from "@/components/InviteFriendsDialog";
import UpgradeModal, { type UpgradeModalTab } from "@/components/UpgradeModal";
import ResumeCheckoutBanner from "@/components/ResumeCheckoutBanner";
import LanguageSuggestionBanner from "@/components/LanguageSuggestionBanner";
import PastDuePaymentBanner from "@/components/PastDuePaymentBanner";
import PendingCancelBanner from "@/components/PendingCancelBanner";
import DiscordButton, {
  DiscordIcon,
  useDiscordJoinTitle,
} from "@/components/DiscordButton";
import { trackDiscordClick } from "@/lib/dataLayer";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLanguageRouter } from "@/hooks/useLanguageRouter";
import { trpc } from "@/lib/trpc";
import { getBasePlan, getPlanTierIndex } from "@/lib/pricingPlans";
import { MobileOpenAppButton } from "@/components/MobileAppPrompt";
import { DISCORD_INVITE_URL } from "@/const";
import {
  GlobalNav,
  useDirectorMobileViewport,
} from "@/components/director/DirectorShell";

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout(props: LayoutProps) {
  const { user, isAuthenticated, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCreditHistory, setShowCreditHistory] = useState(false);
  const [showInviteFriends, setShowInviteFriends] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeDefaultTab, setUpgradeDefaultTab] =
    useState<UpgradeModalTab>("yearly");
  const { t, i18n } = useTranslation();
  const discordJoinTitle = useDiscordJoinTitle();
  const { pathWithoutLang, withLanguagePath } = useLanguageRouter();

  // ?invite=1 deep link (used by the content digest email): open the
  // Invite Friends dialog, or the sign-in modal first when signed out. The
  // param is consumed once so a reload doesn't re-open it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("invite") !== "1") return;
    if (isAuthenticated) {
      setShowInviteFriends(true);
      params.delete("invite");
      const rest = params.toString();
      window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${rest ? `?${rest}` : ""}${window.location.hash}`,
      );
    } else {
      setShowAuthModal(true);
    }
  }, [isAuthenticated]);
  const isDirectorMobile = useDirectorMobileViewport();
  const currentPath = pathWithoutLang.split(/[?#]/)[0];
  const scrollToPricingSection = () => {
    const attemptScroll = (remainingAttempts = 20) => {
      const pricingElement = document.getElementById("pricing");
      if (pricingElement) {
        pricingElement.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      if (remainingAttempts > 0) {
        window.setTimeout(() => attemptScroll(remainingAttempts - 1), 100);
      }
    };

    attemptScroll();
  };

  useEffect(() => {
    if (
      (currentPath === "/" || currentPath === "/ai-music-generator") &&
      window.location.hash === "#pricing"
    ) {
      scrollToPricingSection();
    }
  }, [currentPath, pathWithoutLang]);

  // Fetch subscription for credit display. Credits are live-changing balance
  // state — granted at signup, spent during generation, topped up on purchase —
  // so the header must self-heal rather than fetch once and go stale. Without
  // this, a new user whose header rendered before the starter music-video grant
  // landed (or who spends credits in another tab) keeps seeing a stale balance
  // until a manual reload — the reported "100 music-video credits not showing
  // in the dropdown after registration" symptom. Mirror the notifications query
  // beside it: always refetch on mount and poll every 30s.
  const { data: subscription, isLoading: loadingCredits } =
    trpc.payment.getSubscription.useQuery(undefined, {
      enabled: isAuthenticated,
      refetchOnWindowFocus: true,
      refetchOnMount: "always",
      refetchInterval: isAuthenticated ? 30_000 : false,
    });
  const { data: unreadNotifications = 0 } =
    trpc.notifications.unreadCount.useQuery(undefined, {
      enabled: isAuthenticated,
      refetchInterval: isAuthenticated ? 30_000 : false,
      refetchOnWindowFocus: true,
    });

  // Hide app chrome on dedicated full-screen pages (including language-prefixed URLs).
  const isLandingPage = currentPath === "/";
  // New landing design preview renders its own header/footer.
  const isLandingV2Page = currentPath === "/landing-v2";
  // CreativeTools renders its own sidebar, so tools served at a root URL
  // (/ai-video-ad-generator) must count too — else the app sidebar stacks
  // beside the creative-tools one.
  const isCreativeToolsPage = currentPath === "/creative-tools" ||
    currentPath.startsWith("/creative-tools/") ||
    ["/image-tools", "/video-tools", "/audio-tools"].includes(currentPath) ||
    Object.values(creativeToolRootPaths).includes(currentPath);
  const isSupportPage = currentPath === "/support";
  const isHelpPage = currentPath === "/help";
  const isDownloadPage = currentPath === "/download";
  // McpLanding renders its own CreativeLandingShell (header, rail, footer).
  const isMcpPage = currentPath === "/mcp";
  // DirectorHome renders its own GlobalNav + TopBar, so every route serving it
  // must be listed here — the SEO alias routes included, or the page shows the
  // app chrome stacked on top of Director's own (double sidebar + header).
  const isDirectorPage =
    currentPath === "/ai-music-video-generator" ||
    currentPath.startsWith("/ai-music-video-generator/") ||
    currentPath.startsWith("/ai-music-video/") ||
    currentPath === "/free-ai-music-video-generator" ||
    currentPath === "/suno-to-video" ||
    currentPath === "/ai-music-video-director" ||
    currentPath === "/ai-music-video-workflow" ||
    currentPath === "/how-to-make-ai-music-video" ||
    // Dance product page + its SEO aliases (DirectorHome in the dance variant).
    currentPath === "/ai-dance-video-generator" ||
    currentPath === "/dance-video-generator" ||
    currentPath === "/ai-dance-generator" ||
    currentPath === "/tools/ai-dance-video-generator";
  const isStandaloneToolPage =
    currentPath === "/subscription" ||
    (currentPath === "/tools" || currentPath.startsWith("/tools/")) && !isDirectorPage ||
    ["/ai-singing-video-generator", "/singing-video-generator", "/lyric-video-generator", "/lyrics-video-generator", "/make-photo-dance", "/vocal-remover", "/acapella-extractor", "/instrumental-maker", "/karaoke-maker", "/stem-splitter", "/ai-cover", "/ai-music-cover"].includes(currentPath) ||
    currentPath.startsWith("/ai-cover/");
  if (isStandaloneToolPage) {
    return <><CreativeLandingShell><main className="legacy-tool-content">{props.children}</main></CreativeLandingShell><ResumeCheckoutBanner /></>;
  }
  if (
    isLandingPage ||
    isLandingV2Page ||
    isCreativeToolsPage ||
    isSupportPage ||
    isHelpPage ||
    isDownloadPage ||
    isMcpPage ||
    isDirectorPage
  ) {
    // These chrome-less pages skip the sidebar Layout below, which is where the
    // <main> landmark normally lives — so the homepage shipped no <main>
    // (Lighthouse a11y: landmark-one-main). DownloadApp renders its own <main>;
    // wrap the rest in a display:contents <main> so the landmark exists without
    // changing layout (children lay out exactly as before).
    return (
      <>
        {isDownloadPage ? (
          props.children
        ) : (
          <main className="contents">{props.children}</main>
        )}
        <ResumeCheckoutBanner />
        <LanguageSuggestionBanner />
        <PastDuePaymentBanner />
        <PendingCancelBanner />
      </>
    );
  }
  const showSidebar = !isLandingPage;

  const getUserInitial = () => {
    if (!user?.name) return "U";
    return user.name.charAt(0).toUpperCase();
  };

  const handleOpenOwnProfile = () => {
    if (!user?.id) return;
    window.location.href = withLanguagePath(`/author/${user.id}`);
  };

  const handleOpenNotifications = () => {
    window.location.href = withLanguagePath("/notifications");
  };

  const handleOpenUpgradeModal = (defaultTab: UpgradeModalTab = "yearly") => {
    setUpgradeDefaultTab(defaultTab);
    setShowUpgradeModal(true);
  };

  const credits = subscription?.credits ?? 0;
  const musicVideoCredits = Number(subscription?.musicVideoCredits ?? 0) || 0;
  const basePlan = getBasePlan(subscription?.plan || "free");
  const planLabel =
    basePlan === "pro_v2"
      ? "Pro"
      : basePlan.charAt(0).toUpperCase() + basePlan.slice(1);
  const showUpgradeButton =
    !loadingCredits && getPlanTierIndex(subscription?.plan || "free") < 2;

  const navItems = [
    { label: "Image tools", icon: Sparkles, href: "/image-tools", section: t("sections.create") },
    { label: "Video tools", icon: Video, href: "/video-tools", section: t("sections.create") },
    { label: "Audio tools", icon: Sparkles, href: "/audio-tools", section: t("sections.create") },
    {
      label: t("nav.featured", { defaultValue: "Featured" }),
      icon: Sparkles,
      href: "/featured",
      section: t("sections.explore", { defaultValue: "EXPLORE" }),
    },
    {
      label: t("nav.aiMusic"),
      icon: Music,
      href: "/ai-music-generator",
      section: t("sections.create"),
    },
    {
      label: t("nav.aiCover"),
      icon: Mic2,
      href: "/ai-cover",
      section: t("sections.create"),
    },
    {
      label: t("nav.aiMusicVideo", { defaultValue: "AI Music Video" }),
      icon: Video,
      href: "/ai-music-video-generator",
      section: t("sections.create"),
    },
    {
      label: t("globalNav.nav.createSingingVideo", {
        defaultValue: "Create Singing Video",
      }),
      icon: MicVocal,
      href: "/ai-singing-video-generator",
      section: t("sections.create"),
    },
    {
      label: t("globalNav.nav.createDanceVideo", {
        defaultValue: "Create Dance Video",
      }),
      icon: PersonStanding,
      href: "/ai-dance-video-generator",
      section: t("sections.create"),
    },
    {
      label: t("globalNav.nav.createLyricsVideo", {
        defaultValue: "Create Lyrics Video",
      }),
      icon: Captions,
      href: "/lyric-video-generator",
      section: t("sections.create"),
    },
    {
      label: t("nav.stemSplitter", { defaultValue: "Stem Splitter" }),
      icon: Layers,
      href: "/stem-splitter",
      section: t("sections.tools"),
    },
    {
      label: t("nav.vocalRemover"),
      icon: Radio,
      href: "/vocal-remover",
      section: t("sections.tools"),
    },
    {
      label: t("nav.bpmFinder", { defaultValue: "BPM & Key Finder" }),
      icon: Activity,
      href: "/bpm-finder",
      section: t("sections.tools"),
    },
    {
      label: t("nav.tapTempo", { defaultValue: "Tap Tempo" }),
      icon: Activity,
      href: "/tap-tempo",
      section: t("sections.tools"),
    },
    {
      label: t("nav.keyFinder", { defaultValue: "Song Key Finder" }),
      icon: Music,
      href: "/key-finder",
      section: t("sections.tools"),
    },
    {
      label: t("nav.songAnalyzer", { defaultValue: "Song Analyzer" }),
      icon: Search,
      href: "/song-analyzer",
      section: t("sections.tools"),
    },
    {
      label: t("nav.audioJoiner", { defaultValue: "Audio Joiner" }),
      icon: Link2,
      href: "/audio-joiner",
      section: t("sections.tools"),
    },
    {
      label: t("nav.audioCutter", { defaultValue: "Audio Cutter" }),
      icon: Scissors,
      href: "/audio-cutter",
      section: t("sections.tools"),
    },
    {
      label: t("nav.reverseAudio", { defaultValue: "Reverse Audio" }),
      icon: Undo2,
      href: "/reverse-audio",
      section: t("sections.tools"),
    },
    {
      label: t("nav.audioConverter", { defaultValue: "Audio Converter" }),
      icon: Repeat,
      href: "/audio-converter",
      section: t("sections.tools"),
    },
    {
      label: t("nav.volumeBooster", { defaultValue: "Volume Booster" }),
      icon: Volume2,
      href: "/volume-booster",
      section: t("sections.tools"),
    },
    {
      label: t("nav.slowedReverb", { defaultValue: "Slowed + Reverb" }),
      icon: Waves,
      href: "/slowed-reverb",
      section: t("sections.tools"),
    },
    {
      label: t("nav.audioToMidi", { defaultValue: "Audio to MIDI" }),
      icon: Music2,
      href: "/audio-to-midi",
      section: t("sections.tools"),
    },
    {
      label: t("nav.chordProgressionGenerator", {
        defaultValue: "Chord Progression Generator",
      }),
      icon: Piano,
      href: "/chord-progression-generator",
      section: t("sections.tools"),
    },
    {
      label: t("nav.eightBit", { defaultValue: "8-Bit Music Maker" }),
      icon: Gamepad2,
      href: "/8-bit-converter",
      section: t("sections.tools"),
    },
    {
      label: t("nav.audioMastering", { defaultValue: "Audio Mastering" }),
      icon: SlidersHorizontal,
      href: "/audio-mastering",
      section: t("sections.tools"),
    },
    {
      label: t("nav.songs"),
      icon: ListMusic,
      href: "/songs",
      section: t("sections.library"),
    },
    {
      label: t("nav.projects", { defaultValue: "Projects" }),
      icon: FolderOpen,
      href: "/projects",
      section: t("sections.library"),
    },
    {
      label: t("nav.playlists", { defaultValue: "Playlists" }),
      icon: PlaySquare,
      href: "/playlists",
      section: t("sections.library"),
    },
  ];

  const renderSidebarContent = () => {
    let currentSection = "";
    return (
      <>
        <div className="px-5 py-6">
          <Link href={withLanguagePath("/")}>
            <span className="text-lg font-semibold text-white cursor-pointer hover:text-gray-300 transition-colors">
              Solmi
            </span>
          </Link>
        </div>

        {isAuthenticated && user && (
          <div className="px-5 pb-3">
            <button
              type="button"
              onClick={handleOpenOwnProfile}
              className="flex w-full items-center gap-3 rounded-xl px-1 py-2 text-left transition-colors hover:bg-white/5"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-700 text-base font-semibold text-white">
                {getUserInitial()}
              </div>
              <div className="min-w-0">
                <p className="truncate text-[15px] font-medium text-white">
                  {user.name || user.email?.split("@")[0] || "User"}
                </p>
                <p className="truncate text-xs text-white/55">
                  {subscription?.plan
                    ? subscription.plan.charAt(0).toUpperCase() +
                      subscription.plan.slice(1)
                    : "Free"}
                </p>
              </div>
            </button>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto px-3 py-2">
          {navItems.map((item) => {
            const isActive = pathWithoutLang === item.href;
            const showSection = currentSection !== item.section;
            if (showSection) {
              currentSection = item.section;
            }

            return (
              <React.Fragment key={item.href}>
                {showSection && (
                  <div className="px-3 pt-5 pb-2 text-xs font-medium text-[#6a6a6a] uppercase tracking-wider">
                    {item.section}
                  </div>
                )}
                <Link href={withLanguagePath(item.href)}>
                  <div
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer transition-colors ${
                      isActive
                        ? "text-white bg-white/10"
                        : "text-[#8a8a8a] hover:text-white"
                    }`}
                  >
                    <item.icon className="h-5 w-5" aria-hidden="true" />
                    <span className="text-[15px]">{item.label}</span>
                  </div>
                </Link>
              </React.Fragment>
            );
          })}
        </nav>

        <div className="border-t border-[#1a1a1a]/70 px-4 pb-5 pt-4">
          <div className="flex items-center gap-2">
            <a
              href="https://x.com/solmiai"
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-10 w-10 items-center justify-center rounded-lg text-[#a3a3a3] transition-colors hover:bg-white/5 hover:text-white"
              aria-label="X"
            >
              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M18.901 2H22l-6.77 7.74L23.2 22h-6.24l-4.88-6.22L6.65 22H3.55l7.24-8.28L1.2 2h6.4l4.4 5.67L18.9 2Zm-1.09 18.17h1.72L6.72 3.74H4.87l12.94 16.43Z" />
              </svg>
            </a>
            <a
              href="https://www.youtube.com/@SolmiAI"
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-10 w-10 items-center justify-center rounded-lg text-[#a3a3a3] transition-colors hover:bg-white/5 hover:text-white"
              aria-label="YouTube"
            >
              <Youtube className="h-5 w-5" aria-hidden="true" />
            </a>
            <a
              href={DISCORD_INVITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-10 w-10 items-center justify-center rounded-lg text-[#a3a3a3] transition-colors hover:bg-white/5 hover:text-white"
              aria-label={discordJoinTitle}
              title={discordJoinTitle}
              onClick={() => trackDiscordClick("app_sidebar")}
            >
              <DiscordIcon className="h-5 w-5" />
            </a>
            <a
              href="https://www.tiktok.com/@solmiai"
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-10 w-10 items-center justify-center rounded-lg text-[#a3a3a3] transition-colors hover:bg-white/5 hover:text-white"
              aria-label="TikTok"
            >
              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.35V2h-3.2v13.37a2.89 2.89 0 0 1-2.88 2.88 2.89 2.89 0 0 1 0-5.77c.3 0 .59.05.87.14V9.36a6.1 6.1 0 0 0-.87-.06A6.1 6.1 0 1 0 15.82 15V8.2a8.02 8.02 0 0 0 4.68 1.5V6.69h-.91Z" />
              </svg>
            </a>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="flex h-screen bg-background">
      {showSidebar && !isDirectorMobile && (
        <div className="hidden lg:block">
          <GlobalNav />
        </div>
      )}

      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top Header */}
        <header className="h-14 md:h-16 border-b border-border flex items-center justify-between px-3 md:px-6 gap-2 md:gap-4">
          <div className="flex items-center gap-2">
            {/* Mobile Menu Button - Only show when sidebar should be visible */}
            {showSidebar && (
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="lg:hidden"
                    aria-label="Open menu"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-60 p-0">
                  <SheetHeader className="sr-only">
                    <SheetTitle>Navigation</SheetTitle>
                  </SheetHeader>
                  <div className="flex flex-col h-full">
                    {renderSidebarContent()}
                  </div>
                </SheetContent>
              </Sheet>
            )}
          </div>

          {/* Navigation Links - Centered (only show on landing page) */}
          {isLandingPage && (
            <nav className="hidden lg:flex flex-1 justify-center">
              <div className="flex items-center gap-6">
                <div className="relative group">
                  <button
                    className="text-sm hover:text-primary cursor-pointer transition-colors bg-transparent border-none outline-none p-0 m-0 font-inherit flex items-center gap-1"
                    aria-label={t("nav.tools")}
                    aria-haspopup="true"
                  >
                    {t("nav.tools")}
                    <ChevronDown className="h-3 w-3" aria-hidden="true" />
                  </button>
                  <div className="absolute left-0 top-full mt-2 w-48 bg-gray-900 border border-gray-800 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[100]">
                    <Link href={withLanguagePath("/ai-music-generator")}>
                      <div className="flex items-center px-4 py-2 hover:bg-gray-800 cursor-pointer transition-colors">
                        <Music className="mr-2 h-4 w-4" aria-hidden="true" />
                        <span>{t("nav.aiMusic")}</span>
                      </div>
                    </Link>
                    <Link href={withLanguagePath("/ai-cover")}>
                      <div className="flex items-center px-4 py-2 hover:bg-gray-800 cursor-pointer transition-colors">
                        <Mic2 className="mr-2 h-4 w-4" aria-hidden="true" />
                        <span>{t("nav.aiCover")}</span>
                      </div>
                    </Link>
                    <Link href={withLanguagePath("/vocal-remover")}>
                      <div className="flex items-center px-4 py-2 hover:bg-gray-800 cursor-pointer transition-colors">
                        <Radio className="mr-2 h-4 w-4" aria-hidden="true" />
                        <span>{t("nav.vocalRemover")}</span>
                      </div>
                    </Link>
                    <Link href={withLanguagePath("/stem-splitter")}>
                      <div className="flex items-center px-4 py-2 hover:bg-gray-800 cursor-pointer transition-colors">
                        <Layers className="mr-2 h-4 w-4" aria-hidden="true" />
                        <span>
                          {t("nav.stemSplitter", {
                            defaultValue: "Stem Splitter",
                          })}
                        </span>
                      </div>
                    </Link>
                  </div>
                </div>
                <Link href={withLanguagePath("/ai-music-generator")}>
                  <span className="text-sm hover:text-primary cursor-pointer transition-colors flex items-center">
                    {t("nav.create")}
                  </span>
                </Link>
                <Link href={withLanguagePath("/ai-music-video-generator")}>
                  <span className="text-sm hover:text-primary cursor-pointer transition-colors flex items-center">
                    {t("nav.aiMusicVideo", { defaultValue: "AI Music Video" })}
                  </span>
                </Link>
                <Link href={withLanguagePath("/songs")}>
                  <span className="text-sm hover:text-primary cursor-pointer transition-colors flex items-center">
                    {t("nav.library")}
                  </span>
                </Link>
                <a
                  href={withLanguagePath("/#pricing")}
                  onClick={(e) => {
                    if (
                      currentPath === "/" ||
                      currentPath === "/ai-music-generator"
                    ) {
                      e.preventDefault();
                      window.history.replaceState(
                        null,
                        "",
                        withLanguagePath("/#pricing"),
                      );
                      scrollToPricingSection();
                    }
                    // If not on landing page, let the href handle navigation
                  }}
                  className="text-sm hover:text-primary cursor-pointer transition-colors flex items-center"
                >
                  {t("nav.pricing")}
                </a>
                <Link href={withLanguagePath("/blog/")}>
                  <span className="text-sm hover:text-primary cursor-pointer transition-colors flex items-center">
                    {t("nav.blog")}
                  </span>
                </Link>
              </div>
            </nav>
          )}
          {!isLandingPage && <div className="flex-1"></div>}

          {/* Right Side Actions */}
          <div className="flex items-center gap-1.5">
            <div className="hidden lg:block">
              <DiscordButton placement="app_header" />
            </div>
            {isAuthenticated ? (
              <>
                {/* Upgrade Button for users below Pro */}
                {showUpgradeButton && (
                  <Button
                    onClick={() => handleOpenUpgradeModal("yearly")}
                    size="sm"
                    className="h-7 gap-1.5 rounded-lg bg-[#8b5cf6] px-3 text-[12.5px] font-semibold text-white shadow-none hover:bg-[#a78bfa]"
                  >
                    <Crown className="h-3.5 w-3.5" aria-hidden="true" />
                    <span>Upgrade</span>
                  </Button>
                )}

                <SilentErrorBoundary
                  resetKeys={[
                    isAuthenticated,
                    user?.id,
                    currentPath,
                    i18n.language,
                  ]}
                >
                  <FeedbackDialog />
                </SilentErrorBoundary>

                {/* Language Selector */}
                <div className="hidden md:block">
                  <LanguageSelector />
                </div>

                {/* Credit Display */}
                {loadingCredits ? (
                  <div className="flex items-center gap-2">
                    <Loader2
                      className="h-4 w-4 animate-spin text-muted-foreground"
                      aria-hidden="true"
                    />
                  </div>
                ) : (
                  <HoverCard openDelay={120} closeDelay={150}>
                    <HoverCardTrigger asChild>
                      <button
                        type="button"
                        onClick={() => handleOpenUpgradeModal("yearly")}
                        className="flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
                        aria-label={
                          musicVideoCredits > 0
                            ? `${credits} general credits and ${musicVideoCredits} music video credits, ${planLabel} plan. Open upgrade options.`
                            : `${credits} credits, ${planLabel} plan. Open upgrade options.`
                        }
                      >
                        <Star
                          className="h-3.5 w-3.5 fill-current text-muted-foreground"
                          aria-hidden="true"
                        />
                        <span className="font-semibold tabular-nums">
                          {credits.toLocaleString()}
                        </span>
                        {/* Starter music-video credits live in a separate
                            wallet. Showing them only inside the hover card
                            hid them from anyone on a touch device (Jaynie,
                            Sep 23 2026: "the only credit showing is 16"
                            while 100 video credits sat on the account). */}
                        {musicVideoCredits > 0 ? (
                          <>
                            <Video
                              className="ml-1 h-3.5 w-3.5 text-indigo-300"
                              aria-hidden="true"
                            />
                            <span className="font-semibold tabular-nums">
                              {musicVideoCredits.toLocaleString()}
                            </span>
                          </>
                        ) : null}
                      </button>
                    </HoverCardTrigger>
                    <HoverCardContent
                      align="end"
                      sideOffset={12}
                      className="w-72 rounded-2xl border border-white/10 bg-[#202029] p-3 text-white shadow-2xl"
                    >
                      <div className="px-4 pb-2 pt-1.5">
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2 text-white/70">
                            <Star
                              className="h-3.5 w-3.5 fill-current text-white/40"
                              aria-hidden="true"
                            />
                            General credits
                          </span>
                          <span className="font-semibold tabular-nums">
                            {credits.toLocaleString()}
                          </span>
                        </div>
                        {musicVideoCredits > 0 ? (
                          <>
                            <div className="mt-2 flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2 text-white/70">
                                <Video
                                  className="h-3.5 w-3.5 text-indigo-300"
                                  aria-hidden="true"
                                />
                                Music video credits
                              </span>
                              <span className="font-semibold tabular-nums">
                                {musicVideoCredits.toLocaleString()}
                              </span>
                            </div>
                            <p className="mt-0.5 pl-[22px] text-xs leading-snug text-white/45">
                              New-user starter credits for music videos only.
                            </p>
                          </>
                        ) : null}
                      </div>
                      <div className="my-1 h-px bg-white/10" />
                      <button
                        type="button"
                        onClick={() => handleOpenUpgradeModal("topup")}
                        className="flex w-full items-center gap-4 rounded-xl px-4 py-3 text-left text-base font-medium transition-colors hover:bg-white/[0.08] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
                      >
                        <ShoppingBag
                          className="h-5 w-5 text-white/90"
                          aria-hidden="true"
                        />
                        <span>Get Add-on Credits</span>
                      </button>
                      {import.meta.env.VITE_DISABLE_REFERRAL_PROGRAM !==
                        "true" && (
                        <button
                          type="button"
                          onClick={() => setShowInviteFriends(true)}
                          className="mt-1 flex w-full items-center gap-4 rounded-xl px-4 py-3 text-left text-base font-medium transition-colors hover:bg-white/[0.08] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
                        >
                          <Gift
                            className="h-5 w-5 text-white/90"
                            aria-hidden="true"
                          />
                          <span>Invite Friends, Earn Credits</span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowCreditHistory(true)}
                        className="mt-1 flex w-full items-center gap-4 rounded-xl px-4 py-3 text-left text-base font-medium transition-colors hover:bg-white/[0.08] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
                      >
                        <ReceiptText
                          className="h-5 w-5 text-white/90"
                          aria-hidden="true"
                        />
                        <span>Credit Usage Details</span>
                      </button>
                    </HoverCardContent>
                  </HoverCard>
                )}

                {/* Notification Bell */}
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Notifications"
                  onClick={handleOpenNotifications}
                  className="relative h-9 w-9"
                >
                  <Bell className="h-5 w-5" aria-hidden="true" />
                  {unreadNotifications > 0 ? (
                    <span className="absolute right-1 top-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-white">
                      {unreadNotifications > 99 ? "99+" : unreadNotifications}
                    </span>
                  ) : null}
                </Button>

                {/* Profile Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="h-9 w-9 rounded-full bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold p-0"
                    >
                      {getUserInitial()}
                    </Button>
                  </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <div className="px-2 py-1.5">
                        <p className="text-sm font-medium">{user?.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {user?.email}
                        </p>
                      </div>
                      <DropdownMenuItem
                        onClick={() => {
                          window.location.href = withLanguagePath("/projects");
                        }}
                      >
                        <FolderOpen className="mr-2 h-4 w-4" />
                        <span>
                          {t("nav.projects", { defaultValue: "Projects" })}
                        </span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleOpenOwnProfile}>
                        <UserIcon className="mr-2 h-4 w-4" />
                        <span>My Profile</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          window.location.href =
                            withLanguagePath("/subscription");
                        }}
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        <span>{t("common.manageSubscription")}</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={logout}>
                        <LogOut className="mr-2 h-4 w-4" />
                        <span>{t("common.logout")}</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
              </>
            ) : (
              <>
                <SilentErrorBoundary
                  resetKeys={[
                    isAuthenticated,
                    user?.id,
                    currentPath,
                    i18n.language,
                  ]}
                >
                  <FeedbackDialog />
                </SilentErrorBoundary>
                <div className="hidden md:block">
                  <LanguageSelector />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAuthModal(true)}
                  className="text-xs md:text-sm"
                >
                  {t("common.signIn")}
                </Button>
                <Button
                  size="sm"
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold text-xs md:text-sm"
                  onClick={() => setShowAuthModal(true)}
                >
                  {t("common.signUp")}
                </Button>
              </>
            )}
            <MobileOpenAppButton
              source="layout_header"
              className="inline-flex h-9 items-center justify-center rounded-full bg-gradient-to-r from-violet-500 to-emerald-500 px-3.5 text-sm font-semibold text-white shadow-none md:hidden"
            />
          </div>
        </header>

        {/* Clear the fixed bottom nav, which shows below lg (its wrapper is
            lg:hidden). md:pb-0 left 768–1023px pages with their last ~58px
            under the bar, so bottom links there could not be clicked. */}
        <div
          className={`flex-1 overflow-y-auto min-w-0 ${showSidebar ? "pb-24 lg:pb-0" : ""}`}
        >
          {props.children}
        </div>
      </main>

      {showSidebar && isDirectorMobile && (
        <div className="lg:hidden">
          <GlobalNav />
        </div>
      )}
      {/* Auth Modal */}
      <AuthModal open={showAuthModal} onClose={() => setShowAuthModal(false)} />
      {/* Credit History Modal */}
      <CreditHistoryModal
        open={showCreditHistory}
        onClose={() => setShowCreditHistory(false)}
      />
      <InviteFriendsDialog
        open={showInviteFriends}
        onClose={() => setShowInviteFriends(false)}
      />
      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        trigger="header_credit"
        defaultTab={upgradeDefaultTab}
      />
      <ResumeCheckoutBanner />
      <LanguageSuggestionBanner />
      <PastDuePaymentBanner />
        <PendingCancelBanner />
    </div>
  );
}
